import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled, { css } from 'styled-components';
import {
  Button,
  Buttons,
  CancelButton,
  DeleteConfirmation,
  Dropdown,
  Icon,
  ModalCard,
  SingleSelect,
} from '~/components';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { colors } from '~/styles';
import Assistant from './Assistant';
import { defaultPanelWidth, PANEL_MIN_WIDTH, DRAWER_WIDTH, useAssistant } from './AssistantContext';
import Sidebar from './Sidebar';

const SIDEBAR_EXPANDED_WIDTH = '10.3125rem';
const SIDEBAR_COLLAPSED_WIDTH = '3.125rem';
const DRAG_MIN = PANEL_MIN_WIDTH;
const HEADER_HEIGHT = '3.125rem';
// Ideal expanded width: chat max-width (820) + drag handle area (12) + sidebar collapsed (48) + borders (2)
const CHAT_EXPANDED_WIDTH = 882;

const S = {
  Panel: styled.div`
    position: fixed;
    top: ${({ $hasMessage }) => ($hasMessage ? `calc(2.5rem + ${HEADER_HEIGHT})` : HEADER_HEIGHT)};
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    background: ${colors.white};
    border-left: 1px solid ${colors.grey10};
    z-index: ${({ $mode }) => ($mode === 'panel' ? 55 : 70)};
    transform: translateX(${({ $open }) => ($open ? '0' : '100%')});
    transition: transform 150ms ease-in-out, width 150ms ease-in-out, left 150ms ease-in-out;

    ${({ $mode, $sidebarExpanded, $widthOverride }) => {
      if ($mode === 'full') {
        const sidebarWidth = $sidebarExpanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH;
        return css`
          left: ${sidebarWidth};
          width: auto;
        `;
      }
      if ($mode === 'drawer') {
        const sidebarWidth = $sidebarExpanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH;
        return css`
          width: clamp(${DRAWER_WIDTH}px, 75vw, 1600px);
          max-width: calc(100vw - ${sidebarWidth});
          top: 0;
        `;
      }
      // panel mode (resizable)
      const sidebarWidth = $sidebarExpanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH;
      if ($widthOverride) {
        return css`
          width: ${$widthOverride}px;
          max-width: min(75vw, calc(100vw - ${sidebarWidth}));
          transition: transform 150ms ease-in-out, left 150ms ease-in-out;
        `;
      }
      return css`
        width: max(${PANEL_MIN_WIDTH}px, 25vw);
        max-width: min(75vw, calc(100vw - ${sidebarWidth}));
      `;
    }}
  `,

  PanelHeader: styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0 0.75rem;
    height: ${HEADER_HEIGHT};
    flex-shrink: 0;

    ${({ $noBorder }) =>
      !$noBorder &&
      css`
        border-bottom: 1px solid ${colors.grey10};
      `}
  `,

  HeaderActions: styled.div`
    display: flex;
    align-items: center;
    gap: 0.125rem;
    margin-left: auto;
  `,

  ModeButton: styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    min-width: 1.75rem;
    padding: 0;
    border: none;
    border-radius: 0.25rem;
    background: ${({ $active }) => ($active ? colors.grey10 : 'transparent')};
    color: ${({ $active }) => ($active ? colors.primary : colors.grey40)};
    cursor: pointer;
    font-size: 0.75rem;

    &:hover {
      background: ${colors.grey5};
      color: ${colors.primary};
    }
  `,

  PanelBody: styled.div`
    flex: 1;
    display: flex;
    flex-direction: row;
    overflow: hidden;
    position: relative;
    ${({ $mode }) => $mode === 'panel' && 'padding-left: 12px;'}
  `,

  ChatColumn: styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  `,

  CloseButton: styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    border: none;
    border-radius: 0.25rem;
    background: transparent;
    color: ${colors.danger};
    cursor: pointer;
    font-size: 0.75rem;
    padding: 0;

    &:hover {
      background: ${colors.grey5};
      color: ${colors.danger};
    }
  `,

  DragHandle: styled.div`
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: 12px;
    cursor: col-resize;
    z-index: 75;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${colors.grey5};
    border-left: 1px solid ${colors.grey10};
    border-right: 1px solid ${colors.grey10};
    transition: background 0.15s ease, border-color 0.15s ease;

    &::after {
      content: '⋮';
      font-size: 16px;
      color: ${colors.grey40};
      transition: color 0.15s ease;
    }

    &:hover,
    &:active {
      background: ${colors.primary5};
      border-color: ${colors.primary25};

      &::after {
        color: ${colors.primary};
      }
    }
  `,

  DrawerOverlay: styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 69;
  `,

  SettingsDropdown: styled(Dropdown)`
    .settings-menu {
      min-width: 12rem;
    }

    .settings-menu .dropdown-item {
      font-size: 0.8125rem;
    }
  `,

  MenuItem: styled(Dropdown.Item)`
    font-size: 0.8125rem;

    .icon {
      font-size: 0.75rem;
    }
  `,
};

export default function AssistantPanel({ sidebarExpanded }) {
  const api = useApi();
  const { workspace, hasMessage } = useWorkspace();
  const confirmation = useConfirmation();
  const toast = useToast();
  const {
    isOpen,
    mode,
    setMode,
    closePanel,
    panelWidthOverride,
    setPanelWidthOverride,
    sidebarOpen,
    toggleSidebar,
    profiles,
    profileId,
    setProfileId,
  } = useAssistant();

  // Lifted conversation state
  const [conversations, setConversations] = useState({ data: [], isReady: false, error: null });
  const [conversationId, setConversationId] = useState(null);

  // Assistant profile modal
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [pendingProfileId, setPendingProfileId] = useState(profileId);

  const fetchConversations = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).ai().conversations().list();
    setConversations({ data, isReady: true, error: null });
    return data;
  }, [api, workspace.id]);

  const conversationIdRef = useRef(conversationId);
  conversationIdRef.current = conversationId;

  useEffect(() => {
    if (!isOpen) return;
    fetchConversations().then((data) => {
      if (conversationIdRef.current || !data?.length) return;
      const latest = data[0];
      const minutesAgo = (Date.now() - new Date(latest.updatedAt).getTime()) / 60000;
      if (minutesAgo <= 5) setConversationId(latest.id);
    });
  }, [isOpen, fetchConversations]);

  const handleNewChat = useCallback(() => {
    setConversationId(null);
  }, []);

  const handleSelectConversation = useCallback((id) => {
    setConversationId(id);
  }, []);

  const handleRenameConversation = useCallback(
    async (id, title) => {
      await api.www.workspaces(workspace.id).ai().conversations(id).rename({ title });
      setConversations((prev) => ({
        ...prev,
        data: prev.data.map((c) => (c.id === id ? { ...c, title } : c)),
      }));
    },
    [api, workspace.id],
  );

  const handleDeleteConversation = useCallback(
    async (id) => {
      await confirmation.prompt((resolve) => (
        <DeleteConfirmation
          resolve={async (confirm) => {
            if (!confirm) {
              resolve(false);
              return;
            }
            await api.www.workspaces(workspace.id).ai().conversations(id).delete();
            setConversations((prev) => ({
              ...prev,
              data: prev.data.filter((c) => c.id !== id),
            }));
            if (conversationId === id) {
              setConversationId(null);
            }
            resolve(true);
            toast.success('Conversation deleted.');
          }}>
          Are you sure you want to delete this conversation?
        </DeleteConfirmation>
      ));
    },
    [api, workspace.id, conversationId, confirmation, toast],
  );

  // Drag-to-resize
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const handleMouseDown = useCallback(
    (e) => {
      if (mode !== 'panel') return;
      e.preventDefault();
      isDragging.current = true;
      startX.current = e.clientX;
      startWidth.current = panelWidthOverride || defaultPanelWidth();

      const handleMouseMove = (moveEvent) => {
        if (!isDragging.current) return;
        const delta = startX.current - moveEvent.clientX;
        const dragMax = Math.floor(window.innerWidth * 0.75);
        const newWidth = Math.min(dragMax, Math.max(DRAG_MIN, startWidth.current + delta));
        setPanelWidthOverride(newWidth);
      };

      const handleMouseUp = () => {
        isDragging.current = false;
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [mode, panelWidthOverride, setPanelWidthOverride],
  );

  const panelRef = useRef(null);

  const handleDoubleClick = useCallback(() => {
    if (mode !== 'panel') return;
    const dragMax = Math.floor(window.innerWidth * 0.75);
    const quarter = Math.min(dragMax, Math.max(DRAG_MIN, Math.floor(window.innerWidth * 0.25)));
    const expanded = Math.min(dragMax, Math.max(DRAG_MIN, CHAT_EXPANDED_WIDTH));
    const current = panelWidthOverride || defaultPanelWidth();
    const target = Math.abs(current - expanded) < Math.abs(current - quarter) ? quarter : expanded;

    const el = panelRef.current;
    if (el) {
      el.style.transition = 'width 150ms ease-in-out, transform 150ms ease-in-out, left 150ms ease-in-out';
      const onEnd = () => {
        el.style.transition = '';
        el.removeEventListener('transitionend', onEnd);
      };
      el.addEventListener('transitionend', onEnd);
    }
    setPanelWidthOverride(target);
  }, [mode, panelWidthOverride, setPanelWidthOverride]);

  const isFull = mode === 'full';
  const isDrawer = mode === 'drawer';
  const isOverlayMode = isFull || isDrawer;

  return (
    <>
      {/* Drawer overlay */}
      {isOpen && isDrawer && ReactDOM.createPortal(<S.DrawerOverlay onClick={closePanel} />, document.body)}

      <S.Panel
        ref={panelRef}
        data-testid="assistant-panel"
        $open={isOpen}
        $mode={mode}
        $sidebarExpanded={sidebarExpanded}
        $hasMessage={hasMessage}
        $widthOverride={panelWidthOverride}>
        {/* Drag handle — only in panel mode */}
        {mode === 'panel' && <S.DragHandle onMouseDown={handleMouseDown} onDoubleClick={handleDoubleClick} />}

        <S.PanelBody $mode={mode}>
          <S.ChatColumn>
            <S.PanelHeader $noBorder={isOverlayMode}>
              <S.HeaderActions>
                <S.SettingsDropdown align="right">
                  {({ setIsOpen, isOpen: dropdownOpen }) => (
                    <>
                      <Dropdown.Trigger>
                        <S.ModeButton $active={dropdownOpen} onClick={() => setIsOpen(!dropdownOpen)} title="Settings">
                          <Icon icon="cog" />
                        </S.ModeButton>
                      </Dropdown.Trigger>
                      <Dropdown.Menu className="settings-menu">
                        <S.MenuItem
                          onClick={() => {
                            toggleSidebar();
                            setIsOpen(false);
                          }}>
                          Chat History
                        </S.MenuItem>
                        {profiles.length > 1 && (
                          <S.MenuItem
                            onClick={() => {
                              setPendingProfileId(profileId);
                              setShowProfileModal(true);
                              setIsOpen(false);
                            }}>
                            Assistant Profile
                          </S.MenuItem>
                        )}
                      </Dropdown.Menu>
                    </>
                  )}
                </S.SettingsDropdown>
                <S.ModeButton
                  $active={mode === 'drawer'}
                  onClick={() => setMode(mode === 'drawer' ? 'panel' : 'drawer')}
                  title={mode === 'drawer' ? 'Panel' : 'Drawer'}>
                  <Icon icon="sidebar-flip" />
                </S.ModeButton>
                <S.ModeButton
                  $active={mode === 'full'}
                  onClick={() => setMode(mode === 'full' ? 'panel' : 'full')}
                  title={mode === 'full' ? 'Panel' : 'Full screen'}>
                  <Icon icon="expand" />
                </S.ModeButton>
                <S.CloseButton data-testid="assistant-panel-close" onClick={closePanel} title="Close">
                  <Icon icon="xmark" />
                </S.CloseButton>
              </S.HeaderActions>
            </S.PanelHeader>
            {isOpen && (
              <Assistant
                conversationId={conversationId}
                onConversationIdChange={setConversationId}
                onConversationsChange={setConversations}
              />
            )}
          </S.ChatColumn>
          <Sidebar
            mode={mode}
            conversations={conversations.data}
            activeConversationId={conversationId}
            onSelectConversation={handleSelectConversation}
            onNewChat={handleNewChat}
            onRenameConversation={handleRenameConversation}
            onDeleteConversation={handleDeleteConversation}
            sidebarOpen={sidebarOpen}
            onToggleSidebar={toggleSidebar}
          />
        </S.PanelBody>
      </S.Panel>

      {/* Assistant profile modal */}
      {showProfileModal && (
        <ModalCard title="Assistant Profile" onClose={() => setShowProfileModal(false)}>
          <ModalCard.Body>
            <SingleSelect
              name="profile-select"
              placeholder="Assistant Profile"
              materialPlaceholder=""
              value={pendingProfileId}
              onChange={({ value }) => setPendingProfileId(value)}>
              {profiles.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </SingleSelect>
          </ModalCard.Body>
          <ModalCard.Footer>
            <Buttons align="right">
              <CancelButton onClick={() => setShowProfileModal(false)}>Cancel</CancelButton>
              <Button
                onClick={() => {
                  setProfileId(pendingProfileId);
                  setShowProfileModal(false);
                }}>
                Apply
              </Button>
            </Buttons>
          </ModalCard.Footer>
        </ModalCard>
      )}
    </>
  );
}
